import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { makeStyles } from "@mui/styles";
import { AppContext } from '../../../../RoutesWithAuth';
import axios from '../../../common/AxiosConfig';
import { LoadingIndicator } from '../reports/reconcilationreport/MuiStyled';
import CustomGrid from '../customgrid';
import EditDialog from '../customgrid/EditDialog';
import { IconButton, Tooltip, Tab, Tabs } from '@mui/material';
import { Edit } from '@mui/icons-material';
import uniq from 'lodash/uniq';
import { RequestorMapping } from '../../../common/commons';
import { ADMIN_PERMISSIONS } from '../../../common/RolePermissions';
import PermissionButton from '../PermissionButton';

const ScopeOptions = ['Yes', 'No'];

const EDIT_FIELDS = [
    { name: 'display', label: 'App Name', type: 'text', disabled: true },
    { name: 'requestor', label: 'Requestor', type: 'text', disabled: true },
    { name: 'routing_automated', label: 'Routing Automated', type: 'text', disabled: true },
    { name: 'awl_scope', label: 'AWL', type: 'select', options: ScopeOptions, required: true },
    { name: 'deactivate_account_scope', label: 'DA', type: 'select', options: ScopeOptions, required: true },
    { name: 'isr_scope', label: 'ISR', type: 'select', options: ScopeOptions, required: true },
    { name: 'rtc_scope', label: 'RTC', type: 'select', options: ScopeOptions, required: true },
    { name: 'rtd_scope', label: 'RTD', type: 'select', options: ScopeOptions, required: true },
    { name: 'rtk_scope', label: 'RTK', type: 'select', options: ScopeOptions, required: true },
    { name: 'rtoo_scope', label: 'RTO', type: 'select', options: ScopeOptions, required: true },
    { name: 'rtt_scope', label: 'RTT', type: 'select', options: ScopeOptions, required: true },
    { name: 'rwl_scope', label: 'RWL', type: 'select', options: ScopeOptions, required: true },
    { name: 'tvla_scope', label: 'TVLA', type: 'select', options: ScopeOptions, required: true },
    { name: 'ufv_scope', label: 'UFV', type: 'select', options: ScopeOptions, required: true },
];

const useStyles = makeStyles(() => ({
    upstreamsWrapper: {
        marginTop: '4px',
        marginRight: '15px',
        height: 'calc(100% - 50px)'
    },
    tabBody: {
        marginTop: '12px'
    }
}));

const ROUTING_TAB = {
    REQUEST: 'REQUEST',
    RESPONSE: 'RESPONSE'
};

const getRoutingType = (commType) => (String(commType || '').toUpperCase() === ROUTING_TAB.REQUEST ? ROUTING_TAB.REQUEST : ROUTING_TAB.RESPONSE);

function Upstreams() {
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const isAdmin = context.isAdmin.get;
    const classes = useStyles();

    const [appsData, setAppsData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [activeRoutingTab, setActiveRoutingTab] = useState(ROUTING_TAB.REQUEST);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const fetchData = () => {
        setIsLoading(true);
        axios.get('apps', {
            headers: { Authorization: authToken },
            params: { type: 'upstream' }
        }).then(res => {
            const data = Array.isArray(res.data) ? res.data : [];
            data.sort((a, b) => {
                const nameCompare = (a.display || '').localeCompare(b.display || '');
                if (nameCompare !== 0) return nameCompare;
                const reqCompare = (a.requestor || '').localeCompare(b.requestor || '');
                if (reqCompare !== 0) return reqCompare;
                const routeA = getRoutingType(a.comm_type) === ROUTING_TAB.REQUEST ? 'Request Automated' : 'Response Automated';
                const routeB = getRoutingType(b.comm_type) === ROUTING_TAB.REQUEST ? 'Request Automated' : 'Response Automated';
                return routeA.localeCompare(routeB);
            });
            setAppsData(data);
            setIsLoading(false);
        }).catch(err => {
            setIsLoading(false);
            console.log(err);
        });
    };

    const handleEditClick = useCallback((row) => {
        setEditingRow(row);
        setEditDialogOpen(true);
    }, []);

    const handleEditSave = useCallback((formValues, originalRow) => {
        setIsLoading(true);
        axios.put(`apps/upstream/${originalRow.originalId}`, {
            awl_scope: formValues.awl_scope,
            deactivate_account_scope: formValues.deactivate_account_scope,
            rwl_scope: formValues.rwl_scope,
            ufv_scope: formValues.ufv_scope,
            rtc_scope: formValues.rtc_scope,
            tvla_scope: formValues.tvla_scope,
            isr_scope: formValues.isr_scope,
            rtd_scope: formValues.rtd_scope,
            rtk_scope: formValues.rtk_scope,
            rtoo_scope: formValues.rtoo_scope,
            rtt_scope: formValues.rtt_scope
        }, {
            headers: { Authorization: authToken }
        }).then(() => {
            fetchData();
        }).catch(err => {
            setIsLoading(false);
            console.log(err);
        });
        setEditDialogOpen(false);
        setEditingRow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const handleEditClose = useCallback(() => {
        setEditDialogOpen(false);
        setEditingRow(null);
    }, []);

    const gridData = useMemo(() => {
        return appsData.map((row, i) => ({
            id: row.id + row.requestor,
            rowId: i + 1,
            originalId: row.id,
            display: row.display,
            requestor: row.requestor,
            routing_type: getRoutingType(row.comm_type),
            routing_automated: getRoutingType(row.comm_type) === ROUTING_TAB.REQUEST ? 'Request Automated' : 'Response Automated',
            awl_scope: row.awl_scope ? 'Yes' : 'No',
            deactivate_account_scope: row.deactivate_account_scope ? 'Yes' : 'No',
            isr_scope: row.isr_scope ? 'Yes' : 'No',
            rtc_scope: row.rtc_scope ? 'Yes' : 'No',
            rtd_scope: row.rtd_scope ? 'Yes' : 'No',
            rtk_scope: row.rtk_scope ? 'Yes' : 'No',
            rtoo_scope: row.rtoo_scope ? 'Yes' : 'No',
            rtt_scope: row.rtt_scope ? 'Yes' : 'No',
            rwl_scope: row.rwl_scope ? 'Yes' : 'No',
            tvla_scope: row.tvla_scope ? 'Yes' : 'No',
            ufv_scope: row.ufv_scope ? 'Yes' : 'No',
        }));
    }, [appsData]);

    const filteredGridData = useMemo(() => {
        return gridData
            .filter((row) => row.routing_type === activeRoutingTab)
            .map((row, index) => ({ ...row, rowId: index + 1 }));
    }, [gridData, activeRoutingTab]);

    const columns = useMemo(() => {
        const appNames = uniq(filteredGridData.map(r => r.display));
        const requestors = uniq(filteredGridData.map(r => r.requestor));

        const cols = [{
            name: "#",
            mapping: "rowId",
            width: 50,
            frozen: true,
            sortable: false,
            hideable: false,
            disableColumnMenu: true,
            align: 'center',
            renderCell: ({ rowIndex }) => rowIndex + 1
        }, {
            name: "App Name",
            mapping: "display",
            width: 160,
            frozen: true,
            sortable: true,
            hideable: false,
            type: 'singleSelect',
            valueOptions: appNames,
            displayTootip: ["display"]
        }, {
            name: "Requestor",
            mapping: "requestor",
            width: 140,
            sortable: true,
            type: 'singleSelect',
            valueOptions: requestors
        }, {
            name: "Routing Automated",
            mapping: "routing_automated",
            width: 170,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ['Request Automated', 'Response Automated']
        }, {
            name: "AWL",
            mapping: "awl_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "DA",
            mapping: "deactivate_account_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "ISR",
            mapping: "isr_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "RTC",
            mapping: "rtc_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "RTD",
            mapping: "rtd_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "RTK",
            mapping: "rtk_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "RTO",
            mapping: "rtoo_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "RTT",
            mapping: "rtt_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "RWL",
            mapping: "rwl_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "TVLA",
            mapping: "tvla_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }, {
            name: "UFV",
            mapping: "ufv_scope",
            width: 80,
            sortable: true,
            type: 'singleSelect',
            valueOptions: ScopeOptions
        }];


            cols.push({
                name: "Edit",
                mapping: "actions",
                width: 70,
                sortable: false,
                hideable: false,
                disableColumnMenu: true,
                align: 'center',
                renderCell: ({ row }) => (
                    <Tooltip title="Edit">
                        <PermissionButton
                            iconOnly
                            permission={activeRoutingTab === ROUTING_TAB.REQUEST ? ADMIN_PERMISSIONS.UPSTREAMS_REQ_EDIT : ADMIN_PERMISSIONS.UPSTREAMS_RES_EDIT}
                            size="small"
                            data-testid="edit"
                            onClick={(e) => { e.stopPropagation(); handleEditClick(row); }}
                        >
                            <Edit fontSize="small" />
                        </PermissionButton>
                    </Tooltip>
                )
            });


        return cols;
    }, [isAdmin, handleEditClick, filteredGridData]);

    return (
        <div className={classes.upstreamsWrapper}>
            {isLoading && (<LoadingIndicator />)}

            <Tabs
                variant="scrollable"
                value={activeRoutingTab}
                onChange={(_event, newValue) => setActiveRoutingTab(newValue)}
                aria-label="Upstreams request and response tabs"
                sx={{ borderColor: 'divider' }}
            >
                <Tab label="REQUEST" value={ROUTING_TAB.REQUEST} />
                <Tab label="RESPONSE" value={ROUTING_TAB.RESPONSE} />
            </Tabs>

            <div className={classes.tabBody}>
                <CustomGrid
                    key={activeRoutingTab}
                    columns={columns}
                    rows={filteredGridData}
                    customHeight="calc(100vh - 300px)"
                    enableCustomFiltering={true}
                    showFilterButton={true}
                    showColumnSelector={true}
                />
            </div>
            <EditDialog
                open={editDialogOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
                row={editingRow}
                fields={EDIT_FIELDS}
                title="Edit Upstream App Scope"
                columns={4}
            />
        </div>
    );
}

export default React.memo(Upstreams);
